import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Save,
  CalendarToday,
  VpnKey,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const LABEL_SIZE = '0.8125rem';
const BODY_SIZE = '0.875rem';
const CAPTION_SIZE = '0.75rem';
const HEADING_SIZE = '1rem';

const SectionTitle: React.FC<{ icon: React.ReactElement; label: string }> = ({ icon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
    {React.cloneElement(icon, { sx: { fontSize: 22, color: 'primary.main' } })}
    <Typography sx={{ fontWeight: 700, fontSize: HEADING_SIZE }}>
      {label}
    </Typography>
  </Box>
);

const InfoRow: React.FC<{
  icon?: React.ReactElement;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}> = ({ icon, label, value, mono }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {icon && React.cloneElement(icon, { sx: { fontSize: 15, color: 'text.disabled' } })}
      <Typography sx={{ fontSize: CAPTION_SIZE, color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: BODY_SIZE,
        fontWeight: 600,
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: mono ? 'break-all' : 'normal',
        color: 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ProfileSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', fullName: '' });

  useEffect(() => {
    if (user?.user_metadata) {
      setFormData({
        firstName: user.user_metadata.first_name || '',
        lastName: user.user_metadata.last_name || '',
        fullName: user.user_metadata.full_name || '',
      });
    }
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSaveSuccess(false);
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSaveSuccess(false);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          full_name: formData.fullName || `${formData.firstName} ${formData.lastName}`.trim(),
        },
      });
      if (updateError) throw updateError;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || t('profile.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return t('profile.notAvailable');
    return new Date(dateString).toLocaleString();
  };

  const fieldLabelProps = {
    sx: { fontWeight: 600, fontSize: LABEL_SIZE },
  };

  const fieldInputProps = {
    sx: { fontSize: BODY_SIZE },
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}>
          {t('profile.title')}
        </Typography>
        <Typography sx={{ fontSize: BODY_SIZE, color: 'text.secondary', mt: 0.5 }}>
          {t('profile.subtitle')}
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2.5, fontSize: BODY_SIZE }} onClose={() => setSaveSuccess(false)}>
          {t('profile.updateSuccess')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2.5, fontSize: BODY_SIZE }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── Personal Information ───────────────────────────── */}
        <Card sx={{ flex: '2 1 420px', minWidth: 0 }} variant="outlined">
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <SectionTitle icon={<Person />} label={t('profile.personalInfo')} />

            {/* Avatar row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, fontSize: '1.5rem', flexShrink: 0 }}>
                {formData.fullName?.charAt(0)?.toUpperCase() ||
                  formData.firstName?.charAt(0)?.toUpperCase() ||
                  user?.email?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: HEADING_SIZE, mb: 0.25 }}>
                  {formData.fullName || `${formData.firstName} ${formData.lastName}`.trim() || t('profile.adminUser')}
                </Typography>
                <Typography sx={{ fontSize: CAPTION_SIZE, color: 'text.secondary' }} noWrap>
                  {user?.email}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Form fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label={t('profile.firstName')}
                variant="outlined"
                fullWidth
                size="small"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                InputLabelProps={fieldLabelProps}
                inputProps={fieldInputProps}
              />
              <TextField
                label={t('profile.lastName')}
                variant="outlined"
                fullWidth
                size="small"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                InputLabelProps={fieldLabelProps}
                inputProps={fieldInputProps}
              />
              <TextField
                label={t('profile.fullName')}
                variant="outlined"
                fullWidth
                size="small"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                helperText={t('profile.fullNameHelper')}
                InputLabelProps={fieldLabelProps}
                inputProps={fieldInputProps}
                FormHelperTextProps={{ sx: { fontSize: CAPTION_SIZE } }}
              />

              <Box>
                <Button
                  variant="contained"
                  onClick={handleSaveProfile}
                  disabled={loading}
                  sx={{ fontWeight: 600, fontSize: BODY_SIZE, px: 3, gap: 1 }}
                >
                  {loading
                    ? <CircularProgress size={16} color="inherit" />
                    : <Save sx={{ fontSize: 18 }} />
                  }
                  {loading ? t('profile.saving') : t('profile.saveChanges')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── Account Details ────────────────────────────────── */}
        <Card sx={{ flex: '1 1 280px', minWidth: 0 }} variant="outlined">
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <SectionTitle icon={<Email />} label={t('profile.accountDetails')} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <InfoRow
                icon={<Email />}
                label={t('profile.emailAddress')}
                value={user?.email}
              />

              <Divider sx={{ my: 2 }} />

              <InfoRow
                icon={<VpnKey />}
                label={t('profile.userId')}
                value={user?.id}
                mono
              />

              <Divider sx={{ my: 2 }} />

              <InfoRow
                icon={<CalendarToday />}
                label={t('profile.accountCreated')}
                value={formatDate(user?.created_at)}
              />

              <Divider sx={{ my: 2 }} />

              <InfoRow
                icon={<CalendarToday />}
                label={t('profile.lastSignIn')}
                value={formatDate(user?.last_sign_in_at)}
              />

              <Divider sx={{ my: 2 }} />

              <InfoRow
                icon={<AdminPanelSettings />}
                label={t('profile.role')}
                value={
                  <Typography
                    component="span"
                    sx={{ fontSize: BODY_SIZE, fontWeight: 700, color: 'primary.main' }}
                  >
                    {user?.app_metadata?.role?.toUpperCase() || t('profile.user')}
                  </Typography>
                }
              />
            </Box>
          </CardContent>
        </Card>

      </Box>
    </Box>
  );
};

export default ProfileSettingsPage;
